import { useEffect, useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, ShieldAlert, Users, Mail, KeyRound, Copy, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useUser } from '@/lib/auth';

const PERMISSIONS = [
  { key: 'can_create_events', label: 'Può aggiungere nuovi Eventi' },
  { key: 'can_publish_events', label: "Può approvare/pubblicare Eventi (cambiare stato 'In Revisione')" },
  { key: 'can_manage_locali', label: 'Può modificare/aggiungere Locali' },
  { key: 'can_view_stats', label: 'Può vedere Statistiche e Collaborazioni' },
  { key: 'can_manage_newsletter', label: 'Può gestire gli iscritti alla Newsletter' },
];
const EMPTY = { nome: '', email: '', password: '', ruolo: '', permissions: Object.fromEntries(PERMISSIONS.map(p => [p.key, false])), is_active: true };

export default function AdminTeam() {
  const user = useUser();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [resets, setResets] = useState([]);
  const [smtpOk, setSmtpOk] = useState(true);

  const load = async () => {
    const r = await api.get('/admin/team');
    setItems(r.data || []);
  };

  useEffect(() => {
    if (!user) return;
    if (!user.is_super_admin) return;
    load();
    api.get('/admin/password-resets/recent').then((r) => { setResets(r.data?.resets || []); setSmtpOk(r.data?.smtp_configured); }).catch(() => {});
  }, [user]);

  if (user && !user.is_super_admin) {
    return <AdminLayout title="Accesso negato"><div className="py-16 text-center text-[color:var(--text-muted)]"><ShieldAlert className="w-12 h-12 mx-auto text-red-400 mb-3"/>Solo il Super Admin può gestire il team.</div></AdminLayout>;
  }

  const startNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const startEdit = (u) => {
    setEditing(u);
    setForm({ ...EMPTY, nome: u.nome, email: u.email, ruolo: u.ruolo || '', permissions: { ...EMPTY.permissions, ...(u.permissions || {}) }, is_active: u.is_active, password: '' });
    setOpen(true);
  };

  const save = async () => {
    if (!form.nome || !form.email) { toast.error('Nome e email obbligatori'); return; }
    if (!editing && (!form.password || form.password.length < 8)) { toast.error('Password min. 8 caratteri'); return; }
    setBusy(true);
    try {
      if (editing) {
        const body = { nome: form.nome, ruolo: form.ruolo, permissions: form.permissions, is_active: form.is_active };
        if (form.password) body.password = form.password;
        await api.patch(`/admin/team/${editing.id}`, body);
        toast.success('Profilo aggiornato');
      } else {
        await api.post('/admin/team', { nome: form.nome, email: form.email, password: form.password, ruolo: form.ruolo, permissions: form.permissions, is_active: form.is_active });
        toast.success('Collaboratore creato');
      }
      setOpen(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Errore'); }
    finally { setBusy(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Eliminare ${u.email}?`)) return;
    try { await api.delete(`/admin/team/${u.id}`); toast.success('Eliminato'); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Errore'); }
  };

  const toggleAll = (val) => setForm({...form, permissions: Object.fromEntries(PERMISSIONS.map(p => [p.key, val]))});

  return (
    <AdminLayout title="Gestisci Team">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2 text-sm text-[color:var(--text-muted)]">
          <Users className="w-4 h-4 text-[color:var(--gold)]"/>
          <span>Crea profili per i tuoi collaboratori e definisci esattamente cosa possono fare.</span>
        </div>
        <Button data-testid="team-new-button" onClick={startNew} className="bg-[color:var(--gold-strong)] hover:bg-[#C6A21A] text-black"><Plus className="w-4 h-4 mr-1"/>Nuovo Collaboratore</Button>
      </div>

      <div className="rounded-xl border border-[color:var(--border-c)] bg-[color:var(--surface)] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-[color:var(--text-subtle)] bg-[color:var(--bg-2)]"><tr><th className="p-3">Nome</th><th className="p-3">Email</th><th className="p-3">Ruolo</th><th className="p-3">Permessi</th><th className="p-3">Stato</th><th className="p-3 text-right">Azioni</th></tr></thead>
          <tbody className="divide-y divide-[color:var(--border-c)]">
            {items.length === 0 ? (<tr><td colSpan="6" className="p-6 text-center text-[color:var(--text-muted)]">Nessun membro</td></tr>) : items.map((u) => (
              <tr key={u.id} className="hover:bg-[rgba(214,185,140,0.04)]" data-testid="team-row">
                <td className="p-3"><div className="flex items-center gap-2">{u.is_super_admin && <ShieldAlert className="w-4 h-4 text-[color:var(--gold)]"/>}<span className="font-medium">{u.nome}</span></div></td>
                <td className="p-3 text-[color:var(--text-muted)]">{u.email}</td>
                <td className="p-3 text-[color:var(--text-muted)]">{u.is_super_admin ? <Badge className="bg-[color:var(--gold-strong)] text-black">Super Admin</Badge> : (u.ruolo || '—')}</td>
                <td className="p-3"><div className="flex flex-wrap gap-1">{u.is_super_admin ? <span className="text-xs text-[color:var(--gold)]">Tutti</span> : PERMISSIONS.filter(p => u.permissions?.[p.key]).map(p => <Badge key={p.key} variant="outline" className="text-[10px]">{p.key.replace('can_','').replace(/_/g,' ')}</Badge>)}</div></td>
                <td className="p-3">{u.is_active ? <Badge className="bg-emerald-700/30 text-emerald-300">Attivo</Badge> : <Badge className="bg-red-900/40 text-red-300">Disattivo</Badge>}</td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-2">
                    {!u.is_super_admin && (<>
                      <Button size="sm" variant="ghost" onClick={() => startEdit(u)} data-testid="team-edit"><Pencil className="w-4 h-4"/></Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(u)} data-testid="team-delete" className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4"/></Button>
                    </>)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Recent password reset (for super admin if SMTP not configured) */}
      <div className="mt-6 rounded-xl border border-[color:var(--border-c)] bg-[color:var(--surface)] p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-heading text-lg flex items-center gap-2"><KeyRound className="w-4 h-4 text-[color:var(--gold)]"/>Ultimi reset password</h3>
          {!smtpOk && <Badge variant="outline" className="border-amber-500/40 text-amber-300">SMTP non configurato — link visibile qui</Badge>}
        </div>
        <p className="text-xs text-[color:var(--text-muted)] mb-3">Se SMTP non è configurato, qui trovi i link da inviare manualmente. Configura le variabili SMTP_* in <code>/app/backend/.env</code> per inviare email automatiche.</p>
        {resets.length === 0 ? <p className="text-sm text-[color:var(--text-muted)]">Nessuna richiesta recente</p> : (
          <div className="divide-y divide-[color:var(--border-c)]">
            {resets.map((r) => (
              <div key={r.id} className="py-2 flex items-center justify-between gap-2 flex-wrap" data-testid="reset-row">
                <div className="min-w-0">
                  <div className="text-sm"><Mail className="w-3.5 h-3.5 inline mr-1 text-[color:var(--gold)]"/>{r.email} <span className="text-[color:var(--text-subtle)] text-xs">• {r.delivery_status}{r.used ? ' • usato' : ''}</span></div>
                  <a href={r.reset_url} target="_blank" rel="noreferrer" className="text-xs text-[color:var(--gold)] hover:underline break-all">{r.reset_url}</a>
                </div>
                <Button size="sm" variant="ghost" onClick={() => navigator.clipboard.writeText(r.reset_url).then(() => toast.success('Link copiato'))}><Copy className="w-4 h-4 mr-1"/>Copia</Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[color:var(--surface)] border-[color:var(--border-c)] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editing ? `Modifica ${editing.email}` : 'Nuovo Collaboratore'}</DialogTitle>
            <DialogDescription>Imposta nome, accesso e permessi specifici.</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase tracking-wider text-[color:var(--text-subtle)] mb-1 block">Nome *</Label><Input data-testid="team-form-nome" value={form.nome} onChange={(e) => setForm({...form, nome: e.target.value})} className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"/></div>
            <div><Label className="text-xs uppercase tracking-wider text-[color:var(--text-subtle)] mb-1 block">Email *</Label><Input data-testid="team-form-email" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} disabled={!!editing} className="bg-[color:var(--surface-2)] border-[color:var(--border-c)] disabled:opacity-60"/></div>
            <div><Label className="text-xs uppercase tracking-wider text-[color:var(--text-subtle)] mb-1 block">Ruolo (es. PR / Editor)</Label><Input value={form.ruolo} onChange={(e) => setForm({...form, ruolo: e.target.value})} className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"/></div>
            <div><Label className="text-xs uppercase tracking-wider text-[color:var(--text-subtle)] mb-1 block">{editing ? 'Nuova password (lascia vuota per non cambiare)' : 'Password *'}</Label><Input data-testid="team-form-password" type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} minLength={editing ? 0 : 8} className="bg-[color:var(--surface-2)] border-[color:var(--border-c)]"/></div>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-[color:var(--border-c)] bg-[color:var(--surface-2)] p-3 mt-2">
            <Label className="font-medium">Account attivo</Label>
            <Switch checked={form.is_active} onCheckedChange={(v) => setForm({...form, is_active: v})} data-testid="team-form-active"/>
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-heading text-lg text-[color:var(--gold)]">Permessi</h4>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant="ghost" onClick={() => toggleAll(true)}><CheckCircle2 className="w-3 h-3 mr-1"/>Tutti</Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => toggleAll(false)}>Nessuno</Button>
              </div>
            </div>
            <div className="space-y-2">
              {PERMISSIONS.map((p) => (
                <div key={p.key} className="flex items-center justify-between rounded-lg border border-[color:var(--border-c)] bg-[color:var(--surface-2)] p-3">
                  <Label className="text-sm flex-1 pr-3">{p.label}</Label>
                  <Switch data-testid={`team-form-perm-${p.key}`} checked={!!form.permissions[p.key]} onCheckedChange={(v) => setForm({...form, permissions: {...form.permissions, [p.key]: v}})}/>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="team-form-save" onClick={save} disabled={busy} className="bg-[color:var(--gold-strong)] hover:bg-[#C6A21A] text-black">{busy ? 'Salvataggio…' : 'Salva'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
